import React, { FunctionComponent, useEffect } from "react";
import styles from "./Video.module.scss";
import VideoProps from "./VideoProps.interface";

import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import getVideoAspectRatio from "app/media/helpers/getVideoAspectRatio";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";
import VideoEmbed from "components/VideoEmbed/VideoEmbed";
import VideoViewer from "components/VideoViewer/VideoViewer";
import isBrowserContext from "helpers/isBrowserContext";

const Video: FunctionComponent<VideoProps> = (props) => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const videoAspectRatio = getVideoAspectRatio(props.settings.videoCrop, "Widescreen (16:9)");
    const videoSizeClass = props.settings.videoSize ? styles[props.settings.videoSize] : undefined;

    const isAutoplay = props.content.video && props.content.video[0] && props.content.video[0].content.videoAutoplay ? props.content.video[0].content.videoAutoplay : false;
    const isLoop = props.content.video && props?.content.video[0] && props.content.video[0].content.videoLoop ? props.content.video[0].content.videoLoop : false;
    const showControls = !isAutoplay;

    // ANIMATION
    const controls = useAnimation();
    const { ref, inView } = useInView({ threshold: 0.3 });

    const animateUp = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: 80, opacity: 0 }
    };
    const animateDown = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: -80, opacity: 0 }
    };

    const selectAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content._contentType : undefined;
    const selectVariants = selectAnimation === "animateUp" ? animateUp : selectAnimation === "animateDown" ? animateDown : undefined;
    const repeatAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content.repeatAnimation : false;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
        if (!inView && repeatAnimation) {
            controls.start("hidden");
        }
    }, [controls, inView]);

    return (
        <motion.section className={combineClasses([styles.wrapper, videoSizeClass, ...wrapperClasses])} id={identifier} ref={ref} initial={isBrowserContext() ? "hidden" : "shown"} animate={controls} variants={selectVariants} transition={{ duration: 0.4 }}>
            <div className={combineClasses([styles.container, videoSizeClass])}>
                <div className={styles.media}>
                    {/* Media */}
                    {props.content.video && props.content.video[0] && props.content.video[0].content.video?.umbracoFile ? (
                        <VideoViewer src={props.content.video[0].content.video?.umbracoFile} alt={props.content.videoAlternative} autoplay={isAutoplay} loop={isLoop} controls={showControls} aspectRatioX={videoAspectRatio.aspectRatioX} aspectRatioY={videoAspectRatio.aspectRatioY} />
                    ) : null}

                    {/* Video Embed */}
                    {props.content.video && props.content.video[0] && props.content.video[0].content.videoUrl ? (
                        <VideoEmbed src={props.content.video[0].content.videoUrl} alt={props.content.videoAlternative} autoplay={isAutoplay} loop={isLoop} aspectRatioX={16} aspectRatioY={9} />
                    ) : null}

                    {/* Caption */}
                    {props.content.videoCaption && props.content.videoCaption.length > 0 ? (
                        <div className={styles.caption}>{props.content.videoCaption}</div>
                    ) : null}
                </div>
            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </motion.section>
    );
};

export default Video;